import { type Colors } from '@factorialco/deprecated-design-system'
import { BarDatum } from '@nivo/bar'

import type { OriginalDataItem, TooltipData } from './types'
import { getGroupedModeColor } from './utils'

export const generateTooltipData = (
  groupMode: 'grouped' | 'stacked',
  data: Record<string, unknown>,
  stackId: string,
  bar: BarDatum,
  originalData: OriginalDataItem[],
  keys: string[],
  colors: Colors[],
  color: unknown
): TooltipData => {
  const barColor = getGroupedModeColor(groupMode, color)

  if (!bar || !data) {
    return {
      title: stackId,
      mode: 'grouped',
      value: 0,
      barColor,
    }
  }

  const isStacked = groupMode === 'stacked'

  // Extract value from data
  const getBarValue = (): number => {
    if (data && typeof data === 'object') {
      const dataKeys = Object.keys(data).filter(
        (key) =>
          typeof data[key] === 'number' &&
          !key.startsWith('_') &&
          key !== 'bucket' &&
          key !== 'color'
      )

      if (dataKeys.length > 0) {
        const matchingKey = dataKeys.find(
          (key) =>
            String(key).toLowerCase().includes(stackId.toLowerCase()) ||
            stackId.toLowerCase().includes(String(key).toLowerCase())
        )
        const selectedKey = matchingKey || dataKeys[0]
        const value = selectedKey ? data[selectedKey] : undefined
        return typeof value === 'number' ? value : 0
      }
    }
    return typeof bar.value === 'number' ? bar.value : 0
  }

  const value = getBarValue()

  // Return grouped mode data
  if (!isStacked) {
    return {
      title: stackId,
      mode: 'grouped',
      value,
      barColor,
    }
  }

  // Return stacked mode data with series
  if (!originalData?.length || !keys?.length) {
    return {
      title: stackId,
      mode: 'stacked',
      value,
      series: [],
      total: value,
      barColor,
    }
  }

  const foundItem = originalData.find((item) => {
    if (!item || typeof item !== 'object') return false
    const itemBucket = item.bucket || item.name || item.label
    return itemBucket && String(itemBucket).toLowerCase() === stackId.toLowerCase()
  })

  if (!foundItem) {
    return {
      title: stackId,
      mode: 'stacked',
      value,
      series: [],
      total: value,
      barColor,
    }
  }

  const series = keys
    .map((key, index) => {
      const seriesValue = foundItem[key]
      if (typeof seriesValue === 'number' && seriesValue > 0) {
        return {
          name: key,
          value: seriesValue,
          color: colors[index % colors.length] ?? 'gray400',
        }
      }
      return null
    })
    .filter((item): item is { name: string; value: number; color: Colors } => item !== null)

  const total = series.reduce((sum, serie) => sum + (serie?.value || 0), 0)

  return {
    title: stackId,
    mode: 'stacked',
    value,
    series,
    total,
    barColor,
  }
}
